// Realtime client: subscribes to /ws and drives TanStack Query cache invalidation so every view
// (board, list, table, detail) updates live without polling. Mirrors realtime/app.py's frames.
//
// Contract:
//   * On `hello` we record latest_event_id as our cursor.
//   * On `event` we invalidate the affected queries and advance the cursor.
//   * On `resync` we invalidate EVERYTHING and reconnect our cursor to the server head — the
//     server is telling us we may have a gap (queue overflow, cursor too old, listener blip).
//   * On `ping` we reply with a pong; the ping also feeds the liveness watchdog.
//   * On socket close we reconnect with backoff, passing last_event_id so the server replays what
//     we missed while disconnected (making a brief blip invisible).
//
// Liveness watchdog: the server pings every ~25s. If NO frame (ping or event) arrives within the
// watchdog window (~45s) the socket is silently dead — a proxy dropped the pipe without a close
// event, which happens behind some reverse proxies. We force a reconnect so live updates recover
// instead of going quietly stale. The reconnect replays missed events by last_event_id, so no gap.

import { useEffect, useRef } from "react";
import type { QueryClient } from "@tanstack/react-query";
import type { RealtimeFrame } from "./types";

export interface RealtimeStatus {
  connected: boolean;
  lastEventId: number;
}

const WATCHDOG_MS = 45_000;

export function useRealtime(qc: QueryClient, onStatus?: (s: RealtimeStatus) => void): void {
  const cursorRef = useRef(0);
  const closedRef = useRef(false);

  useEffect(() => {
    closedRef.current = false;
    let ws: WebSocket | null = null;
    let backoff = 1000;
    let reconnectTimer: number | undefined;
    let watchdog: number | undefined;

    const invalidateForEvent = (action: string, entityId: string | null) => {
      // Any work_item mutation can affect board/list/table aggregates — invalidate + actively
      // refetch the mounted views so the change lands without a manual reload.
      const opts = { refetchType: "active" as const };
      void qc.invalidateQueries({ queryKey: ["board"], ...opts });
      void qc.invalidateQueries({ queryKey: ["work-items"], ...opts });
      void qc.invalidateQueries({ queryKey: ["table"], ...opts });
      void qc.invalidateQueries({ queryKey: ["today"], ...opts });
      if (entityId && (action.includes("updated") || action.includes("moved") || action.includes("created"))) {
        void qc.invalidateQueries({ queryKey: ["work-item", entityId], ...opts });
      }
    };

    const armWatchdog = () => {
      if (watchdog) window.clearTimeout(watchdog);
      watchdog = window.setTimeout(() => {
        // No frame in the window: the socket is dead-but-not-closed. Drop it and reconnect.
        if (ws && ws.readyState === WebSocket.OPEN) ws.close();
      }, WATCHDOG_MS);
    };

    const connect = () => {
      if (closedRef.current) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const devToken = import.meta.env.DEV ? localStorage.getItem("ac_dev_token") : null;
      const tokenParam = devToken ? `&access_token=${encodeURIComponent(devToken)}` : "";
      ws = new WebSocket(`${proto}://${location.host}/ws?last_event_id=${cursorRef.current}${tokenParam}`);

      ws.onopen = () => {
        backoff = 1000;
        armWatchdog();
        onStatus?.({ connected: true, lastEventId: cursorRef.current });
      };
      ws.onmessage = (ev) => {
        armWatchdog(); // any frame proves liveness
        let frame: RealtimeFrame;
        try {
          frame = JSON.parse(ev.data) as RealtimeFrame;
        } catch {
          return;
        }
        switch (frame.type) {
          case "hello":
            cursorRef.current = frame.latest_event_id;
            if (frame.resync_required) void qc.invalidateQueries();
            onStatus?.({ connected: true, lastEventId: cursorRef.current });
            break;
          case "event":
            cursorRef.current = Math.max(cursorRef.current, frame.id);
            invalidateForEvent(frame.action, frame.entity_id);
            onStatus?.({ connected: true, lastEventId: cursorRef.current });
            break;
          case "resync":
            void qc.invalidateQueries();
            if (typeof frame.id === "number" && frame.id > 0) cursorRef.current = frame.id;
            break;
          case "ping":
            ws?.send(JSON.stringify({ type: "pong" }));
            break;
        }
      };
      ws.onclose = () => {
        if (watchdog) window.clearTimeout(watchdog);
        onStatus?.({ connected: false, lastEventId: cursorRef.current });
        if (closedRef.current) return;
        reconnectTimer = window.setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 30000);
      };
      ws.onerror = () => ws?.close();
    };

    connect();

    return () => {
      closedRef.current = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (watchdog) window.clearTimeout(watchdog);
      ws?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc]);
}
